import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Bug, CheckCircle2, RefreshCw, EyeOff, Eye } from "lucide-react";
import {
  listErrorEvents,
  errorEventStats,
  updateErrorEventStatus,
  type ErrorEventRow,
} from "@/lib/error-events.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/app/page-header";

export const Route = createFileRoute("/_authenticated/errors")({
  component: ErrorsPage,
});

type StatusFilter = "open" | "resolved" | "ignored" | "all";
type SourceFilter = "all" | "server-fn" | "public-api" | "ssr" | "client" | "manual";
type SeverityFilter = "all" | "info" | "warn" | "error" | "critical";

const SEVERITY_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  info: "secondary",
  warn: "outline",
  error: "destructive",
  critical: "destructive",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ErrorsPage() {
  const router = useRouter();
  const qc = useQueryClient();
  const listFn = useServerFn(listErrorEvents);
  const statsFn = useServerFn(errorEventStats);
  const updateFn = useServerFn(updateErrorEventStatus);

  const [status, setStatus] = useState<StatusFilter>("open");
  const [source, setSource] = useState<SourceFilter>("all");
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [active, setActive] = useState<ErrorEventRow | null>(null);

  const list = useQuery({
    queryKey: ["error-events", status, source, severity],
    queryFn: () => listFn({ data: { status, source, severity, limit: 100 } }),
  });

  const stats = useQuery({
    queryKey: ["error-event-stats"],
    queryFn: () => statsFn(),
  });

  const mut = useMutation({
    mutationFn: (vars: { id: string; status: "open" | "resolved" | "ignored" }) =>
      updateFn({ data: vars }),
    onSuccess: (_d, vars) => {
      toast.success(
        vars.status === "resolved"
          ? "Als gelöst markiert."
          : vars.status === "ignored"
            ? "Ignoriert — keine weiteren Alerts."
            : "Wieder offen.",
      );
      qc.invalidateQueries({ queryKey: ["error-events"] });
      qc.invalidateQueries({ queryKey: ["error-event-stats"] });
      if (active && active.id === vars.id) setActive(null);
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : String(e));
    },
  });

  const s = stats.data;
  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Laufzeit-Fehler"
        description="Server-Functions, Webhooks, SSR und Client-Fehler — zusammengefasst & alarmiert."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Offene Gruppen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="size-5 text-destructive" />
              {s?.openCount ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">Treffer (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{s?.totalHits ?? "—"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">Quellen (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs space-y-0.5">
              {s && Object.keys(s.bySource).length > 0 ? (
                Object.entries(s.bySource).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span>{k}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))
              ) : (
                <span className="text-muted-foreground">keine</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Severity (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs space-y-0.5">
              {s && Object.keys(s.bySeverity).length > 0 ? (
                Object.entries(s.bySeverity).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span>{k}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))
              ) : (
                <span className="text-muted-foreground">keine</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Fehler-Gruppen</CardTitle>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger className="h-8 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Status: offen</SelectItem>
                <SelectItem value="resolved">Status: gelöst</SelectItem>
                <SelectItem value="ignored">Status: ignoriert</SelectItem>
                <SelectItem value="all">Status: alle</SelectItem>
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={(v) => setSource(v as SourceFilter)}>
              <SelectTrigger className="h-8 w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Quelle: alle</SelectItem>
                <SelectItem value="server-fn">Server-Fn</SelectItem>
                <SelectItem value="public-api">Public API</SelectItem>
                <SelectItem value="ssr">SSR</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="manual">Manuell</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severity} onValueChange={(v) => setSeverity(v as SeverityFilter)}>
              <SelectTrigger className="h-8 w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Severity: alle</SelectItem>
                <SelectItem value="critical">critical</SelectItem>
                <SelectItem value="error">error</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="info">info</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                list.refetch();
                stats.refetch();
                router.invalidate();
              }}
            >
              <RefreshCw className="size-3.5 mr-1" />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <div className="text-sm text-muted-foreground">Lädt…</div>
          ) : list.error ? (
            <div className="text-sm text-destructive">{(list.error as Error).message}</div>
          ) : (list.data ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Keine Fehler in diesem Filter — alles sauber.
            </div>
          ) : (
            <div className="space-y-2">
              {(list.data ?? []).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setActive(row)}
                  className="w-full text-left rounded-md border border-border bg-card hover:bg-accent/30 transition px-3 py-2.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={SEVERITY_VARIANT[row.severity] ?? "secondary"}>
                          {row.severity}
                        </Badge>
                        <Badge variant="outline">{row.source}</Badge>
                        {row.status !== "open" && <Badge variant="secondary">{row.status}</Badge>}
                        <span className="font-mono text-xs text-muted-foreground">
                          {row.error_name ?? "Error"}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-medium truncate">{row.message}</div>
                      <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>×{row.occurrences}</span>
                        <span>Zuletzt: {fmtDate(row.last_seen_at)}</span>
                        {row.route && <span>Route: {row.route}</span>}
                        {row.server_fn_id && <span>ServerFn: {row.server_fn_id}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge variant={SEVERITY_VARIANT[active?.severity ?? "error"] ?? "secondary"}>
                {active?.severity}
              </Badge>
              <Badge variant="outline">{active?.source}</Badge>
              <span className="font-mono text-sm">{active?.error_name}</span>
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3 text-sm">
              <div className="font-medium">{active.message}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Auftritte:</span>{" "}
                  <span className="font-mono">{active.occurrences}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>{" "}
                  <span>{active.status}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Erstmals:</span>{" "}
                  {fmtDate(active.first_seen_at)}
                </div>
                <div>
                  <span className="text-muted-foreground">Zuletzt:</span>{" "}
                  {fmtDate(active.last_seen_at)}
                </div>
                <div>
                  <span className="text-muted-foreground">Letzter Alarm:</span>{" "}
                  {fmtDate(active.last_alerted_at)}{" "}
                  <span className="text-muted-foreground">(×{active.alert_count})</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Request-ID:</span>{" "}
                  <span className="font-mono">{active.request_id ?? "—"}</span>
                </div>
                {active.route && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Route:</span>{" "}
                    <span className="font-mono">
                      {active.method ? active.method + " " : ""}
                      {active.route}
                    </span>
                  </div>
                )}
                {active.server_fn_id && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">ServerFn:</span>{" "}
                    <span className="font-mono">{active.server_fn_id}</span>
                  </div>
                )}
              </div>
              {active.stack && (
                <details open className="rounded border border-border bg-muted/30 p-2">
                  <summary className="cursor-pointer text-xs font-semibold">Stacktrace</summary>
                  <pre className="mt-2 max-h-[260px] overflow-auto text-[11px] leading-snug whitespace-pre-wrap">
                    {active.stack}
                  </pre>
                </details>
              )}
              {active.context && (
                <details className="rounded border border-border bg-muted/30 p-2">
                  <summary className="cursor-pointer text-xs font-semibold">Kontext</summary>
                  <pre className="mt-2 max-h-[200px] overflow-auto text-[11px] leading-snug whitespace-pre-wrap">
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(active.context), null, 2);
                      } catch {
                        return active.context;
                      }
                    })()}
                  </pre>
                </details>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {active.status !== "resolved" && (
                  <Button
                    size="sm"
                    onClick={() => mut.mutate({ id: active.id, status: "resolved" })}
                    disabled={mut.isPending}
                  >
                    <CheckCircle2 className="size-3.5 mr-1" />
                    Als gelöst markieren
                  </Button>
                )}
                {active.status !== "ignored" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => mut.mutate({ id: active.id, status: "ignored" })}
                    disabled={mut.isPending}
                  >
                    <EyeOff className="size-3.5 mr-1" />
                    Ignorieren (keine Alerts)
                  </Button>
                )}
                {active.status !== "open" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => mut.mutate({ id: active.id, status: "open" })}
                    disabled={mut.isPending}
                  >
                    <Eye className="size-3.5 mr-1" />
                    Wieder öffnen
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
